import React, { useCallback, useEffect, useState } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import { AlertCircle, CheckCircle2, Loader2 } from "lucide-react";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { getApiErrorMessage } from "../lib/apiClient";
import { formatCurrency } from "../lib/utils";
import { useAuth } from "../context/AuthContext";
import { BrandLogo } from "../components/shared/BrandLogo";
import { PaymentReceipt, receiptFromLink } from "../components/payments/PaymentReceipt";

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api/v2`;

export default function PayLink() {
  const { token } = useParams();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const [state, setState] = useState({ loading: true, error: null, link: null });
  const [paying, setPaying] = useState(false);
  const [paid, setPaid] = useState(null);
  const [cashDone, setCashDone] = useState(false);

  const load = useCallback(async () => {
    setState({ loading: true, error: null, link: null });
    try {
      const { data } = await axios.get(`${API_BASE}/payments/links/${token}`);
      setState({ loading: false, error: null, link: data });
      return data;
    } catch (error) {
      setState({ loading: false, error: getApiErrorMessage(error, "This payment link could not be found."), link: null });
      return null;
    }
  }, [token]);

  useEffect(() => {
    const sessionId = searchParams.get("session_id");
    load().then((link) => {
      if (sessionId && link && link.status !== "paid") {
        axios
          .post(`${API_BASE}/payments/links/${token}/confirm`, null, { params: { session_id: sessionId } })
          .then(({ data }) => { if (data.status === "paid") { setPaid({ status: "paid" }); load(); } })
          .catch(() => {});
      }
    });
  }, [load, searchParams, token]);

  const handlePay = async () => {
    setPaying(true);
    try {
      if (state.link.provider === "stripe") {
        const { data } = await axios.post(`${API_BASE}/payments/links/${token}/checkout-session`);
        window.location.href = data.checkout_url;
        return;
      }
      const { data } = await axios.post(`${API_BASE}/payments/links/${token}/pay`);
      setPaid(data);
    } catch (error) {
      setState((s) => ({ ...s, error: getApiErrorMessage(error, "Could not complete this payment.") }));
    } finally {
      setPaying(false);
    }
  };

  const handlePayCash = async () => {
    setPaying(true);
    try {
      await axios.post(`${API_BASE}/payments/links/${token}/pay-cash`);
      setCashDone(true);
    } catch (error) {
      setState((s) => ({ ...s, error: getApiErrorMessage(error, "Could not record your cash payment choice.") }));
    } finally {
      setPaying(false);
    }
  };

  const link = state.link;
  const isPaid = paid?.status === "paid" || link?.status === "paid";
  const cashChosen = cashDone || (link?.status === "pending" && String(link?.payment_method) === "1");
  const isInvalid = !isPaid && link && (link.status === "cancelled" || link.status === "expired");

  return (
    <div className="flex min-h-screen items-center justify-center bg-primary px-4" data-testid="pay-link-page">
      <div className="w-full max-w-sm">
        <div className="mb-8 flex items-center justify-center">
          <BrandLogo className="h-14" />
        </div>
        <Card className="shadow-2xl">
          <CardContent className={isPaid ? "p-5" : "p-8 text-center"}>
            {state.loading ? (
              <div className="flex justify-center py-8"><Loader2 size={28} className="animate-spin text-primary" /></div>
            ) : state.error ? (
              <>
                <AlertCircle size={32} strokeWidth={1.5} className="mx-auto text-danger" />
                <p className="mt-3 text-sm text-text-main" data-testid="pay-link-error">{state.error}</p>
              </>
            ) : isPaid ? (
              <PaymentReceipt
                data={receiptFromLink(link || {})}
                footer={user?.role === "customer" ? (
                  <Link to="/customer" className="mt-3 block" data-testid="pay-link-back-to-account">
                    <Button className="w-full">Go to My Account</Button>
                  </Link>
                ) : (
                  <Link to="/customer/login" className="mt-3 block" data-testid="pay-link-sign-in-cta">
                    <Button className="w-full">Sign in to My Account</Button>
                  </Link>
                )}
              />
            ) : cashChosen ? (
              <>
                <CheckCircle2 size={36} strokeWidth={1.5} className="mx-auto text-primary" />
                <h1 className="mt-3 font-heading text-lg font-semibold text-text-main" data-testid="pay-link-cash-title">
                  Subscription Activated
                </h1>
                <p className="mt-2 text-sm text-text-muted" data-testid="pay-link-cash-message">
                  You&apos;ve chosen to pay by cash. Please keep the exact amount ready — our
                  delivery driver will collect it when your tiffin is delivered, and we&apos;ll
                  mark it received once it reaches our team. Your payment stays pending until then.
                </p>
                {user?.role === "customer" ? (
                  <Link to="/customer" className="mt-6 block" data-testid="pay-link-cash-home">
                    <Button className="w-full">Go to Home</Button>
                  </Link>
                ) : (
                  <Link to="/customer/login" className="mt-6 block" data-testid="pay-link-cash-signin">
                    <Button className="w-full">Sign in to My Account</Button>
                  </Link>
                )}
                {!isInvalid && (
                  <div className="mt-6 border-t border-border pt-4" data-testid="pay-link-cash-switch-online">
                    <p className="text-xs text-text-muted">
                      Changed your mind? You can still pay now with card or Apple Pay.
                    </p>
                    <Button
                      variant="outline"
                      className="mt-3 w-full"
                      onClick={handlePay}
                      disabled={paying}
                      data-testid="pay-link-cash-pay-online-button"
                    >
                      {paying ? <Loader2 size={16} className="mr-2 animate-spin" /> : null}
                      Pay with card or Apple Pay
                    </Button>
                  </div>
                )}
              </>
            ) : isInvalid ? (
              <>
                <AlertCircle size={32} strokeWidth={1.5} className="mx-auto text-text-muted" />
                <h1 className="mt-3 font-heading text-lg font-semibold text-text-main">Link No Longer Valid</h1>
                <p className="mt-1 text-sm text-text-muted" data-testid="pay-link-invalid-message">
                  This payment link has been {link.status}. Please contact us for a new one.
                </p>
              </>
            ) : (
              <>
                <h1 className="font-heading text-lg font-semibold text-text-main">
                  {link.purpose_label || "Payment"} for {link.customer_name || "your subscription"}
                </h1>
                <p className="mt-1 text-sm text-text-muted">The Virsa Tiffin — subscription #{link.subscription_id}</p>
                <div className="mt-6 space-y-1.5 rounded-lg border border-border bg-background p-4 text-left text-sm" data-testid="pay-link-breakdown">
                  <div className="flex justify-between"><span className="text-text-muted">Subtotal</span><span className="text-text-main">{formatCurrency(link.subtotal ?? link.amount)}</span></div>
                  {link.gst_enabled && (
                    <div className="flex justify-between"><span className="text-text-muted">GST ({link.gst_rate}%)</span><span className="text-text-main">+ {formatCurrency(link.gst_amount)}</span></div>
                  )}
                  <div className="flex justify-between border-t border-border pt-1.5">
                    <span className="font-semibold text-text-main">Total</span>
                    <span className="font-heading text-xl font-bold text-primary" data-testid="pay-link-amount">{formatCurrency(link.total ?? link.amount)}</span>
                  </div>
                </div>
                <Button className="mt-6 w-full" onClick={handlePay} disabled={paying} data-testid="pay-link-pay-button">
                  {paying ? <Loader2 size={16} className="mr-2 animate-spin" /> : null}
                  Pay Online
                </Button>
                <Button variant="outline" className="mt-3 w-full" onClick={handlePayCash} disabled={paying} data-testid="pay-link-pay-cash-button">
                  Pay Cash
                </Button>
                <p className="mt-3 text-xs text-text-muted">
                  Pay Online for instant confirmation (secure checkout powered by Stripe), or
                  choose Pay Cash to activate now and settle with our team.
                </p>
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

